import { ROUTE_CATALOG } from './route-catalog.config';

/**
 * Grupos plegables DENTRO de una sección del menú (p. ej. "Reportes" en Auditoría Retail).
 * FUENTE ÚNICA: los leen el menú lateral (MenuService) y el árbol de
 * /admin/paginas, así que los tres muestran lo mismo.
 *
 * Un grupo es solo visual: no existe en Identity y no cambia permisos. Sus páginas siguen
 * perteneciendo a la sección. El orden de las páginas (y la posición del grupo, que es la
 * de su primera página) sale del orden guardado del menú, no de esta lista.
 */
export interface MenuPageGroup {
    key: string;
    label: string;
    icon: string;
    moduleIdentifier: string;
    sectionIdentifier: string;
    /** Páginas del grupo, por su permissionIdentifier en Identity. */
    pageIdentifiers: string[];
}

export const MENU_PAGE_GROUPS: readonly MenuPageGroup[] = [
    {
        key: 'reportes',
        label: 'Reportes',
        icon: 'pi pi-chart-bar',
        moduleIdentifier: 'mercadeo',
        sectionIdentifier: 'auditoria-retail',
        pageIdentifiers: ['kpi', 'mapa', 'auditorias', 'resumen_ejecutivo', 'resumen_vendedor'],
    },
];

const normalize = (value: string | undefined): string => (value ?? '').trim().toLowerCase();

/**
 * Reemplaza los miembros de un grupo por un único padre, en la posición del primero.
 * Los miembros conservan su orden relativo. Si no hay miembros, devuelve la misma lista.
 * La usan el menú lateral y el árbol de /admin/paginas: los dos agrupan igual.
 */
export function groupItems<T>(items: T[], isMember: (item: T) => boolean, createParent: (members: T[]) => T): T[] {
    const firstIndex = items.findIndex(isMember);
    if (firstIndex < 0) {
        return items;
    }
    const rest = items.filter((item) => !isMember(item));
    const insertAt = items.slice(0, firstIndex).filter((item) => !isMember(item)).length;
    rest.splice(insertAt, 0, createParent(items.filter(isMember)));
    return rest;
}

/** El grupo de una página, por identificadores. */
export function findPageGroup(
    moduleIdentifier: string | undefined,
    sectionIdentifier: string | undefined,
    pageIdentifier: string | undefined,
): MenuPageGroup | undefined {
    return MENU_PAGE_GROUPS.find(
        (group) =>
            normalize(group.moduleIdentifier) === normalize(moduleIdentifier) &&
            normalize(group.sectionIdentifier) === normalize(sectionIdentifier) &&
            group.pageIdentifiers.some((page) => normalize(page) === normalize(pageIdentifier)),
    );
}

/** Rutas de las páginas del grupo (el menú lateral agrupa por ruta). */
export function pageGroupRoutes(group: MenuPageGroup): string[] {
    return ROUTE_CATALOG.filter(
        (entry) =>
            normalize(entry.moduleIdentifier) === normalize(group.moduleIdentifier) &&
            normalize(entry.sectionIdentifier) === normalize(group.sectionIdentifier) &&
            group.pageIdentifiers.some((page) => normalize(page) === normalize(entry.pageIdentifier)),
    ).map((entry) => entry.routerLink);
}
